import React, { useEffect, useState } from 'react';
import { useScan } from '../contexts/ScanContext';

// Always-mounted (see App.tsx) floating indicator for the one ScanContext-tracked batch
// scan, if any — survives page navigation and timeframe/mode switches on /patterns, which
// no longer abort tracking (see PotentialPatternsPage's isScanForCurrentView). Lets the user
// see/minimize/dismiss progress regardless of what they're currently looking at; the scan
// itself (and where its result lands) doesn't depend on this widget being open.
function formatScope(meta: { interval: string; rankMode: string; startRank?: number; endRank?: number }): string {
  const modeLabel = meta.rankMode === 'watchlist'
    ? 'Watchlist'
    : meta.rankMode === 'custom'
      ? `Rank ${meta.startRank ?? '?'}-${meta.endRank ?? '?'}`
      : 'Default Mode';
  return `${meta.interval} · ${modeLabel}`;
}

export default function ScanProgressWidget() {
  const { taskId, isScanning, progress, meta } = useScan();
  const [minimized, setMinimized] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  // A new scan always re-surfaces the widget, even if the previous one was dismissed.
  useEffect(() => {
    if (taskId) setDismissed(false);
  }, [taskId]);

  if (!isScanning || dismissed || !meta) return null;

  const pct = progress && progress.total > 0 ? Math.round((progress.current / progress.total) * 100) : 0;

  return (
    <div className="fixed bottom-5 right-5 z-[60] w-72 max-w-[calc(100vw-2.5rem)]">
      <div className="rounded-2xl border border-white/10 bg-[#0a0a0b]/95 backdrop-blur-xl shadow-2xl overflow-hidden">
        {/* Header — always visible, doubles as the minimize/expand toggle */}
        <button
          onClick={() => setMinimized(m => !m)}
          className="w-full flex items-center justify-between gap-3 px-4 py-3 hover:bg-white/[0.03] transition-colors"
        >
          <div className="flex items-center gap-2.5 min-w-0">
            <span className="relative flex h-2 w-2 flex-shrink-0">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
            </span>
            <span className="text-xs font-semibold text-white truncate">
              Scanning {formatScope(meta)}
            </span>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            <span className="text-[11px] font-medium text-emerald-400 tabular-nums">{pct}%</span>
            <svg
              className={`w-3.5 h-3.5 text-white/40 transition-transform ${minimized ? '' : 'rotate-180'}`}
              fill="none" viewBox="0 0 24 24" stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
            <span
              role="button"
              onClick={(e) => { e.stopPropagation(); setDismissed(true); }}
              className="ml-1 w-5 h-5 flex items-center justify-center rounded-md text-white/30 hover:text-white/70 hover:bg-white/10 transition-colors"
              title="Dismiss (scan keeps running in the background)"
            >
              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </span>
          </div>
        </button>

        {/* Body — collapses when minimized */}
        {!minimized && (
          <div className="px-4 pb-4">
            <div className="w-full bg-white/5 rounded-full h-1.5 overflow-hidden border border-white/5 mb-2">
              <div
                className="bg-gradient-to-r from-emerald-600 via-emerald-400 to-emerald-500 h-full rounded-full transition-all duration-500"
                style={{ width: `${pct}%` }}
              />
            </div>
            <div className="flex items-center justify-between text-[11px]">
              <span className="text-white/40 truncate">
                {progress ? `Analyzing ${progress.symbol}` : 'Preparing...'}
              </span>
              {progress && (
                <span className="text-white/25 tabular-nums flex-shrink-0 ml-2">
                  {progress.current} / {progress.total}
                </span>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
